import { type FC, useId } from "react";
import type { ContentFileDto } from "shared";
import "./FilesGrid.css";
import { MediaItem } from "../media/MediaItem/MediaItem";
import { Button } from "./Button";
import { ButtonIcon } from "./ButtonIcon";

type Props = {
	files: Array<ContentFileDto>;
	onClick: (id: number) => void;
	onRemove: (id: number) => void;
	onAdd: () => void;
	onReorder?: (fromIndex: number, toIndex: number) => void;
};

type ItemProps = {
	file: ContentFileDto;
	index: number;
	total: number;
	onClick: (id: number) => void;
	onRemove: (id: number) => void;
	onReorder?: (fromIndex: number, toIndex: number) => void;
};

const GridItem: FC<Readonly<ItemProps>> = ({
	file,
	index,
	total,
	onClick,
	onRemove,
	onReorder,
}) => {
	const label = file.filename || `Media file ${file.id}`;
	const canReorder = Boolean(onReorder) && total > 1;
	return (
		<div className="item">
			<Button
				className="trigger"
				onClick={() => onClick(file.id)}
				aria-label={`Preview ${label}`}
			>
				<MediaItem fileID={file.id} />
			</Button>
			<ButtonIcon
				icon="delete"
				className="delete"
				onClick={() => onRemove(file.id)}
				size={18}
				aria-label={`Remove ${label}`}
			/>
			{canReorder && onReorder && (
				<div className="reorder-controls">
					<ButtonIcon
						icon="chevron_left"
						size={16}
						disabled={index === 0}
						aria-label={`Move ${label} earlier`}
						onClick={(event) => {
							event.stopPropagation();
							if (index > 0) {
								onReorder(index, index - 1);
							}
						}}
					/>
					<span className="reorder-position">{index + 1}</span>
					<ButtonIcon
						icon="chevron_right"
						size={16}
						disabled={index === total - 1}
						aria-label={`Move ${label} later`}
						onClick={(event) => {
							event.stopPropagation();
							if (index < total - 1) {
								onReorder(index, index + 1);
							}
						}}
					/>
				</div>
			)}
		</div>
	);
};

export const FilesGrid: FC<Readonly<Props>> = ({
	files,
	onClick,
	onRemove,
	onAdd,
	onReorder,
}) => {
	const id = useId();
	return (
		<div className="files-grid">
			{files.map((file, index) => (
				<GridItem
					key={`cf-${id}-${file.id}`}
					file={file}
					index={index}
					total={files.length}
					onClick={onClick}
					onRemove={onRemove}
					onReorder={onReorder}
				/>
			))}
			<figure className="item add">
				<Button
					icon="add"
					iconSize={22}
					variant="ghost"
					aria-label="Add image or video"
					className="trigger"
					onClick={onAdd}
				/>
			</figure>
		</div>
	);
};
